import { useState } from 'react';

import { BrainCircuit, Loader2, RotateCcw, Save } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/hooks/use-toast';
import {
  useServiceHistorySettings,
  useUpdateServiceHistorySettings,
} from '@/hooks/useServiceHistory';

/**
 * The instructions the AI follows when it reads service documents. Changes
 * apply to the next "Read files" or "Regenerate"; saved summaries stay as they are.
 */
export const ServiceHistoryInstructionsCard = () => {
  const { data: settings, isLoading } = useServiceHistorySettings();
  const updateSettings = useUpdateServiceHistorySettings();
  const { toast } = useToast();
  // null = unedited, so the text always follows what was last saved.
  const [edited, setDraft] = useState<string | null>(null);
  const draft = edited ?? settings?.system_instruction ?? '';

  const changed = !!settings && draft.trim() !== settings.system_instruction.trim();
  const isDefault = !!settings && draft.trim() === settings.default_instruction.trim();

  const handleSave = async () => {
    await updateSettings.mutateAsync({ system_instruction: draft.trim() });
    setDraft(null);
    toast({ title: 'Instructions saved', description: 'Used from the next read or regenerate.' });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <BrainCircuit className="text-muted-foreground h-5 w-5" />
          <CardTitle className="text-sm">AI instructions</CardTitle>
        </div>
        <CardDescription>
          What the AI is told when it reads service documents. Changes apply to the next Read files
          or Regenerate; summaries already saved don&apos;t change. The output format (dates,
          mileage, services, gaps) is fixed, so keep the field names in the text.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <p className="text-muted-foreground text-sm">Loading…</p>
        ) : (
          <>
            <textarea
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              rows={16}
              aria-label="AI instructions"
              className="border-input bg-background focus-visible:ring-ring w-full rounded-md border p-3 font-mono text-xs leading-relaxed focus-visible:ring-1 focus-visible:outline-none"
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <p className="text-muted-foreground text-xs">
                {settings?.updated_at
                  ? `Last saved ${new Date(settings.updated_at).toLocaleString('en-GB')}${
                      settings.updated_by ? ` by ${settings.updated_by}` : ''
                    }`
                  : 'Using the built-in default'}
                {isDefault && ' · matches the default'}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => settings && setDraft(settings.default_instruction)}
                  disabled={!settings || isDefault || updateSettings.isPending}
                >
                  <RotateCcw className="mr-1 h-4 w-4" />
                  Reset to default
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={!changed || draft.trim().length < 50 || updateSettings.isPending}
                >
                  {updateSettings.isPending ? (
                    <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                  ) : (
                    <Save className="mr-1 h-4 w-4" />
                  )}
                  Save
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
